import React, { useCallback, useEffect, useState } from "react";
import { MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, inr } from "@/lib/api";

export const SupportTicketsTab = () => {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/support-tickets", { params: { status: filter } });
      setTickets(response.data);
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const updateTicket = async (ticket, patch) => {
    try {
      await api.put(`/admin/support-tickets/${ticket.id}`, patch);
      await loadTickets();
      if (patch.reply) setDrafts((current) => ({ ...current, [ticket.id]: "" }));
      toast.success("Support ticket updated");
    } catch (error) {
      toast.error(formatError(error));
    }
  };

  return (
    <section data-testid="support-tickets-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8C6D46]">Customer care</p>
          <h2 className="mt-1 font-serif text-3xl text-[#1E2A24]">Support Tickets</h2>
        </div>
        <select value={filter} onChange={(event) => setFilter(event.target.value)} className="border border-[#D9C3A5] bg-white px-3 py-2 text-sm" data-testid="support-ticket-status-filter">
          <option value="all">All tickets</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="waiting_customer">Waiting for customer</option>
          <option value="resolved">Resolved</option>
        </select>
      </div>
      <div className="mt-6 space-y-4">
        {loading ? <p className="text-sm text-[#64748B]" data-testid="support-tickets-loading">Loading tickets…</p> : null}
        {!loading && tickets.length === 0 ? <p className="text-sm text-[#64748B]" data-testid="support-tickets-empty">No support tickets found.</p> : null}
        {tickets.map((ticket) => (
          <article key={ticket.id} className="border border-[#E2DCD2] bg-white p-5" data-testid={`support-ticket-${ticket.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2"><MessageCircle size={16} className="text-[#C86D42]" /><span className="font-semibold text-[#1E2A24]">{ticket.customer_name}</span></div>
                <p className="mt-1 text-xs text-[#64748B]">{ticket.customer_email || "Guest contact not provided"} · {ticket.category}</p>
              </div>
              <div className="flex gap-2">
                <select value={ticket.priority} onChange={(event) => updateTicket(ticket, { priority: event.target.value })} className="border border-[#E2DCD2] px-2 py-1 text-xs" data-testid={`support-ticket-priority-${ticket.id}`}>
                  <option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
                </select>
                <select value={ticket.status} onChange={(event) => updateTicket(ticket, { status: event.target.value })} className="border border-[#E2DCD2] px-2 py-1 text-xs" data-testid={`support-ticket-status-${ticket.id}`}>
                  <option value="open">Open</option><option value="in_progress">In progress</option><option value="waiting_customer">Waiting for customer</option><option value="resolved">Resolved</option>
                </select>
              </div>
            </div>
            <p className="mt-4 whitespace-pre-wrap text-sm leading-6 text-[#334155]">{ticket.message}</p>
            {ticket.order_context && <div className="mt-4 border border-[#D9C3A5] bg-[#FDFBF7] p-3 text-xs" data-testid={`support-ticket-order-${ticket.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-[#1E2A24]">Order {ticket.order_context.reference} · {inr(ticket.order_context.total)}</p>
                <button type="button" onClick={() => updateTicket(ticket, { status: "in_progress", reply: "Refund review requested by support." })} className="border border-[#C86D42] bg-white px-2 py-1 text-[10px] font-bold text-[#A8522B]" data-testid={`support-ticket-refund-review-${ticket.id}`}>Refund review</button>
              </div>
              <div className="mt-2 grid gap-2 sm:grid-cols-3">
                <p className="capitalize text-[#64748B]">Order: <strong className="text-[#1E2A24]">{ticket.order_context.order_status}</strong></p>
                <p className="capitalize text-[#64748B]">Payment: <strong className="text-[#1E2A24]">{ticket.order_context.payment_status}</strong></p>
                <p className="capitalize text-[#64748B]">Refund: <strong className="text-[#1E2A24]">{ticket.order_context.refund_status}</strong></p>
              </div>
              {ticket.order_context.tracking_number && <p className="mt-2 text-[#64748B]">Tracking: <strong className="text-[#1E2A24]">{ticket.order_context.tracking_number}</strong></p>}
              <div className="mt-3 space-y-1 border-t border-[#E2DCD2] pt-2 text-[#334155]">
                {ticket.order_context.items?.map((item, index) => <p key={`${item.name}-${index}`}>{item.quantity} × {item.name} · {inr(item.price)}</p>)}
              </div>
              {ticket.order_context.source === "latest_order" && <p className="mt-2 text-[10px] text-[#8C6D46]">Latest customer order attached for support review.</p>}
            </div>}
            {ticket.admin_replies?.map((reply) => <div key={reply.id} className="mt-3 border-l-2 border-[#2D5A47] bg-[#F2F7F3] px-3 py-2 text-sm" data-testid={`support-ticket-reply-${reply.id}`}>{reply.message}</div>)}
            <div className="mt-4 flex gap-2">
              <input value={drafts[ticket.id] || ""} onChange={(event) => setDrafts((current) => ({ ...current, [ticket.id]: event.target.value }))} placeholder="Write an internal support reply…" className="min-w-0 flex-1 border border-[#E2DCD2] px-3 py-2 text-sm" data-testid={`support-ticket-reply-input-${ticket.id}`} />
              <button type="button" onClick={() => updateTicket(ticket, { reply: drafts[ticket.id] || "", status: "in_progress" })} disabled={!(drafts[ticket.id] || "").trim()} className="inline-flex items-center gap-1 bg-[#2D5A47] px-3 py-2 text-xs font-semibold text-white disabled:opacity-40" data-testid={`support-ticket-reply-send-${ticket.id}`}><Send size={14} /> Reply</button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};